import React from 'react';
import {
  Edit, SimpleForm, TextInput, useRecordContext, TopToolbar, ShowButton, required, minLength, maxLength,
} from 'react-admin';
import { Alert, Box, Card, CardContent, Stack, Typography } from '@mui/material';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import BolaoStatusChip from './BolaoStatusChip';
import BolaoImageInput from './BolaoImageInput';

const Title = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="h6" component="span">Bolão: {r.name}</Typography>
      <BolaoStatusChip status={r.status} />
    </Box>
  );
};

const EditActions = () => (
  <TopToolbar>
    <ShowButton />
  </TopToolbar>
);

const Section: React.FC<{ title: string; children: React.ReactNode; hint?: string }> = ({ title, children, hint }) => (
  <Card sx={{ mb: 2, width: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: hint ? 0 : 2 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {hint}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const ConditionalFields = () => {
  const r = useRecordContext();
  const status = r?.status ?? 'draft';
  const draftOnly = status === 'draft';
  const processed = status === 'processed';

  if (processed) {
    return (
      <Alert severity="warning" sx={{ width: '100%' }}>
        Este bolão já foi processado. Edição bloqueada — abra um chamado para suporte se for necessário corrigir.
      </Alert>
    );
  }

  return (
    <>
      <Section title="Identificação">
        <TextInput source="name" label="Nome" validate={[required(), minLength(3), maxLength(120)]} fullWidth helperText={false} />
        <TextInput source="description" label="Descrição" multiline rows={3} fullWidth helperText={false} />
        <BolaoImageInput source="imageUrl" label="Banner" kind="banner" helperText="Imagem larga exibida no topo. JPG/PNG/WebP até 5MB." />
      </Section>

      <Section title="Partida" hint={!draftOnly ? 'Times e datas só podem ser editados em rascunho.' : undefined}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <TextInput source="homeTeamName" label="Time da casa" disabled={!draftOnly} validate={[required(), maxLength(80)]} fullWidth helperText={false} />
            <BolaoImageInput source="homeTeamLogo" label="Logo time da casa" kind="home" helperText="Quadrado, fundo transparente" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextInput source="awayTeamName" label="Time visitante" disabled={!draftOnly} validate={[required(), maxLength(80)]} fullWidth helperText={false} />
            <BolaoImageInput source="awayTeamLogo" label="Logo time visitante" kind="away" helperText="Quadrado, fundo transparente" />
          </Box>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%', mt: 1 }}>
          <DateTimeInputUTC
            source="scheduledAt"
            label="Início do jogo"
            disabled={!draftOnly}
            sx={{ flex: 1 }}
            helperText={false}
          />
          <DateTimeInputUTC
            source="guessDeadlineAt"
            label="Limite palpite"
            disabled={!draftOnly}
            sx={{ flex: 1 }}
            helperText={draftOnly ? 'Deve ser <= kickoff' : 'Não editável após ativação'}
          />
        </Stack>
      </Section>
    </>
  );
};

const BolaoEdit: React.FC = (props: any) => (
  <Edit
    {...props}
    resource="boloes"
    title={<Title />}
    actions={<EditActions />}
    mutationMode="pessimistic"
  >
    <SimpleForm sx={{ maxWidth: 920 }}>
      <ConditionalFields />
    </SimpleForm>
  </Edit>
);

export default BolaoEdit;
